import React from 'react';

interface GraphNode {
  index: number;
  next: number | null;
  lang: string;
  word: string;
}

interface GraphVisualizationProps {
  graph: GraphNode[];
  currentWord: string;
  revealedOrigins?: string[];
}

const GraphVisualization: React.FC<GraphVisualizationProps> = ({ graph, currentWord, revealedOrigins }) => {
  if (!graph || graph.length === 0) {
    return null;
  }

  const nodeHeight = 50;
  const nodeWidth = 120;
  const verticalSpacing = 95;
  const svgHeight = graph.length * verticalSpacing + 50;
  const svgWidth = 300;

  const getNodePosition = (index: number) => {
    return {
      x: svgWidth / 2 - nodeWidth / 2,
      y: svgHeight - (index + 1) * verticalSpacing
    };
  };

  const renderArrow = (fromIndex: number, toIndex: number) => {
    const fromPos = getNodePosition(fromIndex);
    const toPos = getNodePosition(toIndex);
    
    const startX = fromPos.x + nodeWidth / 2;
    const startY = fromPos.y + nodeHeight;
    const endX = toPos.x + nodeWidth / 2;
    const endY = toPos.y;

    return (
      <g key={`arrow-${fromIndex}-${toIndex}`}>
        <line
          x1={startX}
          y1={startY}
          x2={endX}
          y2={endY}
          stroke="#333"
          strokeWidth="2"
          markerEnd="url(#arrowhead)"
        />
      </g>
    );
  };

  const renderNode = (node: GraphNode) => {
    const pos = getNodePosition(node.index);
    
    // For index 0 (bottom node), render just the current word text without a box
    if (node.index === 0) {
      return (
        <g key={`node-${node.index}`}>
          <text
            x={pos.x + nodeWidth / 2}
            y={pos.y + nodeHeight / 2 + 5}
            textAnchor="middle"
            fontSize="32"
            fontWeight="600"
            fill="#333333"
            fontFamily="'Poppins', sans-serif"
          >
            {currentWord}
          </text>
        </g>
      );
    }
    
    // For other nodes, check if we should show the language or keep it empty
    const shouldShowLang = !revealedOrigins || revealedOrigins.some(origin => node.lang.toLowerCase() === origin.toLowerCase());
    
    return (
      <g key={`node-${node.index}`}>
        <rect
          x={pos.x}
          y={pos.y}
          width={nodeWidth}
          height={nodeHeight}
          fill="#f0f0f0"
          stroke="#333"
          strokeWidth="2"
          rx="5"
        />
        {shouldShowLang && (
          <text
            x={pos.x + nodeWidth / 2}
            y={pos.y + nodeHeight / 2 + 5}
            textAnchor="middle"
            fontSize="14"
            fontWeight="bold"
            fill="#333"
          >
            {node.lang}
          </text>
        )}
      </g>
    );
  };

  return (
    <div className="graph-visualization">
      <svg width={svgWidth} height={svgHeight}>
        <defs>
          <marker
            id="arrowhead"
            markerWidth="10"
            markerHeight="7"
            refX="9"
            refY="3.5"
            orient="auto"
          >
            <polygon
              points="0 0, 10 3.5, 0 7"
              fill="#333"
            />
          </marker>
        </defs>
        
        {graph.map(node => renderNode(node))}
        
        {graph.map(node => 
          node.next !== null ? renderArrow(node.index, node.next) : null
        )}
      </svg>
    </div>
  );
};

export default GraphVisualization;